import ApartmentIcon from "@mui/icons-material/Apartment";
import Paper from "@mui/material/Paper";
import {
  alpha,
  Box,
  Divider,
  Grid,
  Skeleton,
  SvgIconTypeMap,
  Typography,
  useTheme,
} from "@mui/material";
import { relativeChange } from "../../../helpers/utils";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { getOverallIndustriesResults } from "../../../api/getOverviewData";
import { useTranslation } from "react-i18next";
import { groupedActivityTags } from "../../../helpers/selectOptions";
import { OverridableComponent } from "@mui/material/OverridableComponent";

import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import SavingsIcon from "@mui/icons-material/Savings";
import FactoryIcon from "@mui/icons-material/Factory";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import BiotechIcon from "@mui/icons-material/Biotech";
import LuggageIcon from "@mui/icons-material/Luggage";
import AccessibleIcon from "@mui/icons-material/Accessible";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { t } from "i18next";

export type IndustryBenchMarkRowType = {
  id: number;
  industry: string;
  device: string;
  mli: number;
  mliBenchmark: number;
  pain: number;
  painBenchmark: number;
  riskFactors: string;
};

const deviceDictionary: { [key: string]: string } = {
  Back: "MLI® Back",
  Elbow: "MLI® Elbow",
};

type OverallActivitiesResultsTableProps = {
  height?: number | string;
  projectCodes: string[];
};

type IndustryStyling = {
  icon: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
  name: string;
  iconUrl: string;
};

export type OverallIndustryResultsTableRef = {
  isReady: () => boolean;
  getRows: () => IndustryBenchMarkRowType[];
};
export const industryStylings: { [key: string]: IndustryStyling } = {
  "tags.Cleaning": {
    icon: CleaningServicesIcon,
    name: t("cleaning"),
    iconUrl:
      "https://corsproxy.io/?url=https://precure-cdn.azureedge.net/images/industryIcons/cleaning.svg",
  },
  "tags.SlaughterHouse": {
    icon: SavingsIcon,
    name: t("slaughter-house"),
    iconUrl:
      "https://corsproxy.io/?url=https://precure-cdn.azureedge.net/images/industryIcons/meat.svg",
  },
  "tags.HeavyIndustry": {
    icon: FactoryIcon,
    name: t("heavy-industry"),
    iconUrl:
      "https://corsproxy.io/?url=https://precure-cdn.azureedge.net/images/industryIcons/industry.svg",
  },
  "tags.Kitchen": {
    icon: FastfoodIcon,
    name: t("kitchen-work"),
    iconUrl:
      "https://corsproxy.io/?url=https://precure-cdn.azureedge.net/images/industryIcons/kitchen.svg",
  },
  "tags.Laboratory": {
    icon: BiotechIcon,
    name: t("laboratory-work"),
    iconUrl:
      "https://corsproxy.io/?url=https://precure-cdn.azureedge.net/images/industryIcons/laboratory.svg",
  },
  "tags.LuggageHandling": {
    icon: LuggageIcon,
    name: t("luggage-handling"),
    iconUrl:
      "https://corsproxy.io/?url=https://precure-cdn.azureedge.net/images/industryIcons/luggage.svg",
  },
  "tags.ManualHandlingOfPeople": {
    icon: AccessibleIcon,
    name: t("manual-handling-of-people"),
    iconUrl:
      "https://corsproxy.io/?url=https://precure-cdn.azureedge.net/images/industryIcons/people.svg",
  },
  "tags.Office": {
    icon: ApartmentIcon,
    name: t("office-work"),
    iconUrl:
      "https://corsproxy.io/?url=https://precure-cdn.azureedge.net/images/industryIcons/office.svg",
  },
  "tags.Production": {
    icon: PrecisionManufacturingIcon,
    name: t("production"),
    iconUrl:
      "https://corsproxy.io/?url=https://precure-cdn.azureedge.net/images/industryIcons/factory.svg",
  },
  "tags.Transport": {
    icon: LocalShippingIcon,
    name: t("transportation"),
    iconUrl:
      "https://corsproxy.io/?url=https://precure-cdn.azureedge.net/images/industryIcons/transport.svg",
  },
  "tags.Warehouse": {
    icon: WarehouseIcon,
    name: t("warehouse"),
    iconUrl:
      "https://corsproxy.io/?url=https://precure-cdn.azureedge.net/images/industryIcons/warehouse.svg",
  },
};
const OverallIndustryResultsTable = forwardRef<
  OverallIndustryResultsTableRef,
  OverallActivitiesResultsTableProps
>(function OverallIndustryResultsTable({ height, projectCodes }, ref) {
  if (height === undefined) {
    height = "100%";
  }

  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<IndustryBenchMarkRowType[]>([]);

  const { t } = useTranslation();
  const theme = useTheme();

  useImperativeHandle(ref, () => ({
    getRows: () => rows,
    isReady: () => isLoading,
  }));

  useEffect(() => {
    let controller = new AbortController();
    let isMounted = true;
    const fetchOverallIndustryResults = async () => {
      try {
        setIsLoading(true);

        // Remove data from unselected project codes
        setRows([]);

        // map uses a unique id to prevent duplicates
        const rowMap = new Map<number, IndustryBenchMarkRowType>();
        const mliResultDict: { [key: string]: [number, number] } = {}; // first number is the sum, second is the counter
        const painResultDict: { [key: string]: [number, number] } = {}; // first number is the sum, second is the counter
        const mliBenchmarkResultDict: { [key: string]: number } = {};
        const painBenchmarkResultDict: { [key: string]: number } = {};
        const riskFactorsDict: { [key: string]: string[] } = {};

        for (const pc of projectCodes) {
          const data = await getOverallIndustriesResults(pc, controller);
          for (const industryData of data) {
            const id = industryData.name + " " + industryData.deviceType;

            // Update mliResultDict: sum and count for each id
            if (mliResultDict[id]) {
              mliResultDict[id][0] += industryData.mli;
              mliResultDict[id][1] += 1;

              riskFactorsDict[id] = riskFactorsDict[id].concat(
                industryData.riskFactors
              );
            } else {
              mliResultDict[id] = [industryData.mli, 1];
              riskFactorsDict[id] = industryData.riskFactors;
            }

            // Update painResultDict: sum and count for each id
            if (painResultDict[id]) {
              painResultDict[id][0] += industryData.painLevel;
              painResultDict[id][1] += 1;
            } else {
              painResultDict[id] = [industryData.painLevel, 1];
            }

            mliBenchmarkResultDict[id] = industryData.mliBenchmark;
            painBenchmarkResultDict[id] = industryData.painBenchmark;
          }
        }

        for (const id of Object.keys(mliResultDict)) {
          const industry = id.split(" ")[0];
          if (industry === "tags.Back" || industry === "tags.Elbow") continue;
          const numericId: number = id
            .split("")
            .reduce((acc, char) => acc + char.charCodeAt(0), 0);
          const uniqueRiskFactors = Array.from(new Set(riskFactorsDict[id]));
          const riskFactors: string = uniqueRiskFactors
            .map((riskFactor) => t("tags." + riskFactor, groupedActivityTags))
            .join(", ");
          const formattedIndustryData: IndustryBenchMarkRowType = {
            id: numericId,
            industry: id.split(" ")[0],
            device: id.split(" ")[1],
            mli: mliResultDict[id][0] / mliResultDict[id][1],
            pain: painResultDict[id][0] / mliResultDict[id][1],
            mliBenchmark: mliBenchmarkResultDict[id],
            painBenchmark: painBenchmarkResultDict[id],
            riskFactors: riskFactors,
          };

          rowMap.set(numericId, formattedIndustryData);
        }
        setRows(Array.from(rowMap.values()));
      } catch (error: any) {
        if (error.name === "AbortError") {
          console.log("Fetch aborted");
        } else {
          console.error("Fetch error:", error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchOverallIndustryResults();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [projectCodes]);

  return rows.length > 0 ? (
    <Grid container spacing={3}>
      {rows.map((row) => (
        <Grid item key={row.id} xs={12} sm={12} md={6} lg={4} xl={4}>
          <Paper
            sx={{
              borderRadius: "5px",
              height: height,
              width: "100%",
              position: "relative",
            }}
          >
            <Grid container flexDirection={"column"}>
              <Grid item sx={{ height: 100 }}>
                <Box
                  sx={{
                    backgroundColor: alpha(theme.palette.primaryChart, 0.9),
                    borderRadius: "5px 5px 0 0",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <Grid
                    container
                    width={"100%"}
                    height={"100%"}
                    alignItems={"center"}
                    alignContent={"center"}
                    justifyContent={"center"}
                  >
                    <Grid
                      item
                      xs={4}
                      sm={4}
                      md={4}
                      lg={4}
                      xl={4}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          border: "3px solid white",
                          backgroundColor: alpha("#8AD6D6", 0.9),
                        }}
                      >
                        {/* <ApartmentIcon sx={{ fontSize: 40, color: "white" }} /> */}
                        {(() => {
                          const IconComponent =
                            industryStylings[row.industry].icon;
                          return (
                            <IconComponent
                              sx={{ fontSize: 40, color: "white" }}
                            />
                          );
                        })()}
                      </Box>
                    </Grid>
                    <Grid item xs={8} sm={8} md={8} lg={8} xl={8}>
                      <Grid container flexDirection={"column"}>
                        <Grid item>
                          <Typography variant="caption" sx={{ color: "white" }}>
                            {deviceDictionary[row.device]}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography
                            variant="h5"
                            sx={{
                              color: "white",
                            }}
                          >
                            {industryStylings[row.industry].name}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item marginTop={2}>
                <Grid
                  container
                  flexDirection={"row"}
                  spacing={2}
                  justifyContent={"space-evenly"}
                >
                  <Grid item>
                    <Grid
                      container
                      flexDirection={"column"}
                      justifyItems={"center"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      alignContent={"center"}
                    >
                      <Grid
                        marginBottom={1}
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        xl={12}
                      >
                        <Typography variant="h6">MLI</Typography>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Typography variant="h5">
                          {row.mli.toFixed(1)}
                          {row.mliBenchmark &&
                            ` / BM ${row.mliBenchmark.toFixed(1)}`}
                        </Typography>
                      </Grid>
                      {row.mliBenchmark &&
                      row.mliBenchmark !== undefined &&
                      row.mliBenchmark !== null &&
                      row.mliBenchmark >= 1 &&
                      relativeChange(row.mli, row.mliBenchmark) * 100 > 0 ? (
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                          <Typography variant="body2">
                            {`${relativeChange(
                              row.mli,
                              row.mliBenchmark
                            ).toFixed(1)}% over benchmark`}
                          </Typography>
                        </Grid>
                      ) : (
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                          <Typography variant="body2">
                            {`${-relativeChange(
                              row.mli,
                              row.mliBenchmark
                            ).toFixed(1)}% below benchmark`}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Divider orientation="vertical" variant="middle" />
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      flexDirection={"column"}
                      justifyItems={"center"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      alignContent={"center"}
                    >
                      <Grid
                        marginBottom={1}
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        xl={12}
                      >
                        <Typography variant="h6">Pain</Typography>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Typography variant="h5">
                          {row.pain.toFixed(1)}
                          {row.painBenchmark &&
                            ` / BM ${row.painBenchmark.toFixed(1)}`}
                        </Typography>
                      </Grid>
                      {row.painBenchmark &&
                      row.painBenchmark !== undefined &&
                      row.painBenchmark !== null &&
                      row.painBenchmark >= 1 &&
                      relativeChange(row.pain, row.painBenchmark) * 100 > 0 ? (
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                          <Typography variant="body2">
                            {`${relativeChange(
                              row.pain,
                              row.painBenchmark
                            ).toFixed(1)}% over benchmark`}
                          </Typography>
                        </Grid>
                      ) : (
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                          <Typography variant="body2">
                            {`${-relativeChange(
                              row.pain,
                              row.painBenchmark
                            ).toFixed(1)}% below benchmark`}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ))}
    </Grid>
  ) : isLoading ? (
    <Skeleton
      sx={{
        borderRadius: "5px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
      }}
      animation="pulse"
      variant="rectangular"
      width={"100%"}
      height={height}
    />
  ) : (
    <Box
      sx={{
        borderRadius: "5px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
      }}
      width={"100%"}
      height={height}
      alignContent={"center"}
      justifyContent={"center"}
    >
      <Typography textAlign={"center"} width={"100%"}>
        {t("no-data-to-show")}
      </Typography>
    </Box>
  );
});

export default OverallIndustryResultsTable;
